// =============================================================================
// SupporterBadge — "Gilded" text treatment for site supporters.
//
// Exports only SupporterName. Avatar images are intentionally left unchanged
// so the badge doesn't clash with the wide variety of user avatar colours.
//
// Visual effect:
//   A periodic metallic sheen sweeps across the username once every ~3 seconds.
//   The gradient uses the active theme's --accent and --accent-light CSS custom
//   properties, so the shimmer tints correctly in every theme:
//     darkfeld  → gold sweep
//     arcane    → violet sweep
//     twilight  → indigo sweep
//     ember     → orange sweep
//     void      → fuchsia sweep
//
// Usage:
//   import { SupporterName } from '@/components/Common/SupporterBadge';
//
//   <SupporterName name={displayName} isSupporter={isSupporter} />
//
//   // Any extra classes (font-size, font-weight, truncation, etc.) go in
//   // className and are applied in both the supporter and non-supporter path:
//   <SupporterName name={name} isSupporter={isSupporter} className="text-sm font-medium truncate" />
// =============================================================================

// ── Sliding gradient shimmer text ────────────────────────────────────────────
//
// How it works:
//   background-image: linear-gradient wide enough to contain a bright "flash"
//     accent → accent (flat) → accent-light → white peak → accent-light → accent (flat)
//   background-size: 300% — makes the gradient 3× the element width so only
//     one third is visible at a time.
//   animation (supporter-shimmer): slides background-position from 0% to 100%,
//     moving the visible window from the left (flat accent) through the bright
//     centre (white flash) to the right (flat accent). Loop is seamless because
//     both ends of the gradient are the same flat accent colour.
//   background-clip: text — clips the gradient to the text glyphs only.
//   color: transparent — lets the background show through the glyphs.
//
// All gradient/clip properties are inline styles to avoid Tailwind colour-class
// specificity conflicts (e.g. text-parchment-muted inherited from a parent span).

export function SupporterName({
  name,
  isSupporter,
  className = '',
}: {
  name: string;
  isSupporter: boolean;
  /** Extra classes applied in both paths — size, weight, truncation, etc. */
  className?: string;
}) {
  if (!isSupporter) return <span className={className}>{name}</span>;

  return (
    <span
      className={`inline-block animate-supporter-shimmer ${className}`}
      style={{
        backgroundImage: `linear-gradient(
          90deg,
          rgb(var(--accent))       0%,
          rgb(var(--accent))       28%,
          rgb(var(--accent-light)) 43%,
          rgba(255,255,255,0.92)   50%,
          rgb(var(--accent-light)) 57%,
          rgb(var(--accent))       72%,
          rgb(var(--accent))       100%
        )`,
        backgroundSize: '300% 100%',
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        color: 'transparent',
      }}
    >
      {name}
    </span>
  );
}
